import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import { copyToClipboard } from '../../../utils/clipboard';
import { useCodePeek } from '../code-peek/use-code-peek';
import type { ElectoralCodeLevel } from '../helper';
import { AngleRightSVG, CheckSVG, CopySVG } from '../svgs';
import { showToast } from '../toast';
import { LevelBadge } from './level-badge';

export interface CodeChipProps {
	code?: string | null;
	level: ElectoralCodeLevel;
	/** When false, clicking does NOT open the peek; copy-only. */
	peek?: boolean;
	/** Hide the level badge. */
	compact?: boolean;
}

export const CodeChip: FC<CodeChipProps> = ({ code, level, peek = true, compact = false }) => {
	const [copied, setCopied] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const peekCtx = useCodePeek();

	useEffect(
		() => () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		},
		[]
	);

	if (!code) {
		return <span className='code-chip code-chip--empty'>—</span>;
	}

	const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		const ok = await copyToClipboard(code);
		if (!ok) {
			showToast('error', 'Could not copy to clipboard');
			return;
		}
		setCopied(true);
		showToast('success', `Copied ${code}`);
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => setCopied(false), 1500);
	};

	const handleOpenPeek = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (!peek || !peekCtx) return;
		peekCtx.openPeek(level, code);
	};

	const isPeekable = peek && !!peekCtx;

	return (
		<span className='code-chip'>
			{!compact && <LevelBadge level={level} />}
			{isPeekable ? (
				<button
					type='button'
					className='code-chip__main'
					aria-haspopup='dialog'
					aria-label={`Open ${level} details for ${code}`}
					onClick={handleOpenPeek}
				>
					<span className='code-chip__text'>{code}</span>
					<span
						className='code-chip__chevron'
						aria-hidden='true'
					>
						<AngleRightSVG />
					</span>
				</button>
			) : (
				<span className='code-chip__main code-chip__main--static'>
					<span className='code-chip__text'>{code}</span>
				</span>
			)}
			<button
				type='button'
				className={`code-chip__copy ${copied ? 'is-copied' : ''}`}
				onClick={handleCopy}
				aria-label={`Copy code ${code}`}
				title={copied ? 'Copied' : 'Copy code'}
			>
				{copied ? <CheckSVG color='var(--color-primary, #1B6B4A)' /> : <CopySVG />}
			</button>
		</span>
	);
};

export default CodeChip;
